import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PaymentType as PayableType } from "@/api-v3";
import {
  setReturnTo,
  startClaimDetour,
} from "@/features/payment-methods/store/payment-method-form-slice";
import StorageManager from "@/features/storage";
import { useAppDispatch } from "@/store";
import { AppRoute } from "@/types/routes";

import { canReceivePayableType } from "../../../helpers/allowed-payable-types";
import { usePaymentSetupStatus } from "../../../hooks/use-payment-setup-status";
import { resetMileageClaimDraft } from "../../../store/mileage-claim-draft-slice";
import { forgetPdfReceipt } from "../../../store/pdf-receipt-draft-slice";
import { resetReceiptClaimDraft } from "../../../store/receipt-claim-draft-slice";

import { ProtectYourIdentity } from "./protect-your-identity-dialog";
import { SubmitClaimButton } from "./submit-claim-button";

/** The Payable Item kind each claim flow asks PASS to pay. */
const PAYABLE_TYPE_BY_CLAIM_ROUTE: Partial<Record<AppRoute, PayableType>> = {
  [AppRoute.RECEIPT_SUBMIT]: PayableType.receipt_claim,
  [AppRoute.MILEAGE_SUBMIT]: PayableType.mileage_claim,
};

interface NewClaimMenuProps {
  onBeforeNavigate?: () => void;
}

export const NewClaimMenu = ({ onBeforeNavigate }: NewClaimMenuProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPayoutAccount, activePaymentAccount } = usePaymentSetupStatus();
  const [isProtectYourIdentityOpen, setProtectYourIdentityOpen] =
    useState(false);

  /**
   * The kind this claim needs when the Default Payment Account cannot receive
   * it, or null when nothing stands in the way. A Stipend Card is a perfectly
   * good payout account, so this is the second, narrower question.
   */
  const claimDetourKindFor = (route: AppRoute) => {
    const requiredPayableType = PAYABLE_TYPE_BY_CLAIM_ROUTE[route];
    if (!requiredPayableType) return null;

    return canReceivePayableType(activePaymentAccount, requiredPayableType)
      ? null
      : requiredPayableType;
  };

  const navigateTo = (route: AppRoute) => {
    // Start every claim from a clean slate so a previously abandoned draft never
    // bleeds into a new claim (navigating away clears it too, but resetting on
    // entry is the explicit "start a new one" guarantee). A new Receipt Claim
    // also forgets the PDF Receipt the device kept from an earlier one.
    if (route === AppRoute.MILEAGE_SUBMIT) dispatch(resetMileageClaimDraft());
    if (route === AppRoute.RECEIPT_SUBMIT) {
      dispatch(resetReceiptClaimDraft());
      dispatch(forgetPdfReceipt());
    }

    // A read-only Spend Card cannot receive a reimbursement, so a claim needs a
    // payout account first. useClaimDraftCleanup keeps returnTo alive across the
    // detour and the payment method form navigates back to it on success. A
    // Default Payment Account that cannot receive this kind of claim takes the
    // same detour, with the kind stored so the list can say why.
    let target = route;
    const requiredPayableType = claimDetourKindFor(route);
    if (!hasPayoutAccount) {
      dispatch(setReturnTo(route));
      target = AppRoute.PAYMENT_METHODS_LIST;
    } else if (requiredPayableType) {
      dispatch(startClaimDetour({ returnTo: route, requiredPayableType }));
      target = AppRoute.PAYMENT_METHODS_LIST;
    }

    onBeforeNavigate?.();
    navigate(target);
  };

  const onSubmitMileage = () => {
    navigateTo(AppRoute.MILEAGE_SUBMIT);
  };

  const onSubmitReceipt = () => {
    // A detour leaves payment setup, not a claim form, so the identity dialog
    // waits until the participant is actually entering a claim.
    const detours =
      !hasPayoutAccount || claimDetourKindFor(AppRoute.RECEIPT_SUBMIT) !== null;
    if (detours || StorageManager.hideProtectYourIdentityMessage()) {
      navigateTo(AppRoute.RECEIPT_SUBMIT);
    } else {
      setProtectYourIdentityOpen(true);
    }
  };

  const onContinueProtectYourIdentity = () => {
    setProtectYourIdentityOpen(false);
    navigateTo(AppRoute.RECEIPT_SUBMIT);
  };

  return (
    <>
      <SubmitClaimButton
        onSubmitMileage={onSubmitMileage}
        onSubmitReceipt={onSubmitReceipt}
      />
      <ProtectYourIdentity
        onOpenChange={setProtectYourIdentityOpen}
        isOpen={isProtectYourIdentityOpen}
        onContinue={onContinueProtectYourIdentity}
      />
    </>
  );
};
